import React, { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IoTrash } from 'react-icons/io5';
import { getAgencyProducts } from '~/api/agencies';
import { useGetEtaiement, useGetEtaiementsList } from '~/hooks/etaiements';
import {
  InputNumber,
  StyledSelect,
} from '~/lib/HooksFormFields';
import PreviewField from '~/components/PreviewField';
import type { IAgencyProduct } from '~/types/agencies';
import type { IOption } from '~/types/options';
import { digitFormatter } from '~/utils';
import {
  RENTAL_PRICE_UNIT_OPTIONS,
  computeRentalGrossLineTotal,
  emptyRentalRow,
  getRentalUnitPrice,
  type IEtaiementRentalRow,
} from '~/utils/etaiement-rentals';
import styles from './rentals-form.module.scss';

const buildProductOptions = (agencyProducts: IAgencyProduct[]): IOption[] => (
  (agencyProducts || [])
    .map((ap: IAgencyProduct) => ({
      label: ap.product?.title || '',
      value: String(ap.productId),
    }))
    .sort((a: IOption, b: IOption) => a.label.localeCompare(b.label, 'fr'))
);

const RentalsForm = ({ isPreview }: { isPreview: boolean }) => {
  const { id } = useParams();
  const { control, watch, setValue } = useFormContext();
  const { data: etaiement } = useGetEtaiement(id || '');
  const { data: list = { rental: {} } } = useGetEtaiementsList();

  const agencyId = typeof etaiement?.agency === 'object'
    ? etaiement?.agency?._id
    : etaiement?.agency;

  const { data: agencyProducts = [] } = useQuery({
    queryKey: ['agency-products', { id: agencyId }],
    queryFn: async () => getAgencyProducts({ id: agencyId || '' }),
    enabled: Boolean(agencyId),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rentals',
  });

  const rentals = watch('rentals') || [];
  const durations = list.rental?.durations || [];

  const productOptions = useMemo(
    () => buildProductOptions(agencyProducts),
    [agencyProducts],
  );

  const findAgencyProduct = useCallback((productId: string) => (
    (agencyProducts || []).find(
      (ap: IAgencyProduct) => String(ap.productId) === String(productId),
    )
  ), [agencyProducts]);

  const updateRowPricing = useCallback((index: number, patch?: Partial<IEtaiementRentalRow>) => {
    const row = {
      ...watch(`rentals.${index}`),
      ...patch,
    };
    const agencyProduct = row?.productId
      ? findAgencyProduct(String(row.productId))
      : undefined;
    const unitPrice = getRentalUnitPrice(agencyProduct, row?.priceUnit);
    const lineTotal = computeRentalGrossLineTotal(unitPrice, row?.duration, row?.quantity);
    setValue(`rentals.${index}.unitPrice`, unitPrice, { shouldDirty: true });
    setValue(`rentals.${index}.lineTotal`, lineTotal, { shouldDirty: true });
  }, [findAgencyProduct, setValue, watch]);

  const handleProductChange = (index: number, opt: IOption | null) => {
    const agencyProduct = opt?.value ? findAgencyProduct(String(opt.value)) : undefined;
    setValue(`rentals.${index}.productId`, opt?.value ?? '', { shouldDirty: true });
    setValue(
      `rentals.${index}.productType`,
      agencyProduct?.product?.productType ?? '',
      { shouldDirty: true },
    );
    if (!opt?.value) {
      setValue(`rentals.${index}.priceUnit`, '', { shouldDirty: true });
      setValue(`rentals.${index}.unitPrice`, null, { shouldDirty: true });
      setValue(`rentals.${index}.lineTotal`, null, { shouldDirty: true });
    } else {
      updateRowPricing(index);
    }
  };

  const handlePriceUnitChange = (index: number, opt: IOption | null) => {
    setValue(`rentals.${index}.priceUnit`, String(opt?.value ?? ''), { shouldDirty: true });
    updateRowPricing(index);
  };

  const handleDurationChange = (index: number, opt: IOption | null) => {
    setValue(`rentals.${index}.duration`, opt?.value ?? '', { shouldDirty: true });
    updateRowPricing(index);
  };

  const handleQuantityChange = (index: number, quantity: number | string) => {
    const nextQuantity = quantity === '' || quantity == null ? null : Number(quantity);
    setValue(`rentals.${index}.quantity`, nextQuantity, { shouldDirty: true });
    updateRowPricing(index, { quantity: nextQuantity });
  };

  const getRowProductType = (row: IEtaiementRentalRow): string => {
    if (row.productType?.trim()) return row.productType.trim();
    const agencyProduct = row.productId
      ? findAgencyProduct(String(row.productId))
      : undefined;
    return agencyProduct?.product?.productType?.trim() || '';
  };

  const renderProductTypeSubline = (row: IEtaiementRentalRow) => {
    const productType = getRowProductType(row);
    if (!productType) return null;
    return <span className={styles.productTypeSubline}>{productType}</span>;
  };

  const getDurationLabel = (duration: number | string | undefined) => {
    const opt = durations.find((d: IOption) => String(d.value) === String(duration));
    return opt?.label ? `${opt.label} mois` : '---';
  };

  const getPriceUnitLabel = (priceUnit: string | undefined) => {
    const opt = RENTAL_PRICE_UNIT_OPTIONS.find((o) => o.value === priceUnit);
    return opt?.label || '---';
  };

  const renderUnitPriceHint = (row: IEtaiementRentalRow) => {
    if (row.unitPrice == null || Number(row.unitPrice) <= 0) return null;
    return (
      <span className={styles.cellHint}>
        {digitFormatter?.format(Number(row.unitPrice))} €
      </span>
    );
  };

  const renderLineTotal = (row: IEtaiementRentalRow) => {
    if (row.lineTotal == null || Number(row.lineTotal) <= 0) {
      return <PreviewField label='' value='---' />;
    }
    return (
      <PreviewField
        label=''
        value={`${digitFormatter?.format(Number(row.lineTotal))} €`}
      />
    );
  };

  if (!agencyId) {
    return (
      <div id="location" className={styles.rentals}>
        <h4>Location</h4>
        <p className={styles.emptyHint}>
          Associez une agence au devis pour renseigner les locations.
        </p>
      </div>
    );
  }

  return (
    <div id="location" className={styles.rentals}>
      <h4>Location</h4>
      {fields.length === 0 && !isPreview && (
        <p className={styles.emptyHint}>Aucune location. Ajoutez une ligne pour commencer.</p>
      )}
      {fields.length > 0 && (
        <div className={styles.tableWrapper}>
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th className={styles.priceUnitCell}>Type de prix</th>
                <th className={styles.quantityCell}>Quantité</th>
                <th className={styles.durationCell}>Durée</th>
                <th className={styles.totalCell}>Total</th>
                {!isPreview && <th className={styles.actionsCell} />}
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => {
                const row = rentals[index] || {};
                return (
                  <tr key={field.id}>
                    <td className={styles.productCell}>
                      {isPreview
                        ? (
                          <>
                            <PreviewField
                              label=''
                              value={
                                findAgencyProduct(String(row.productId || ''))?.product?.title
                                || '---'
                              }
                            />
                            {renderProductTypeSubline(row)}
                          </>
                        )
                        : (
                          <>
                            <StyledSelect
                              name={`rentals.${index}.productId`}
                              control={control}
                              options={productOptions}
                              placeholder='Produit'
                              isSearchable
                              menuInPortal
                              handleChange={(opt) => handleProductChange(index, opt)}
                            />
                            {renderProductTypeSubline(row)}
                          </>
                        )
                      }
                    </td>
                    <td className={styles.priceUnitCell}>
                      {isPreview
                        ? (
                          <>
                            <PreviewField label='' value={getPriceUnitLabel(row.priceUnit)} />
                            {renderUnitPriceHint(row)}
                          </>
                        )
                        : (
                          <>
                            <StyledSelect
                              name={`rentals.${index}.priceUnit`}
                              control={control}
                              options={[...RENTAL_PRICE_UNIT_OPTIONS]}
                              placeholder='Type de prix'
                              isDisabled={!row.productId}
                              menuInPortal
                              handleChange={(opt) => handlePriceUnitChange(index, opt)}
                            />
                            {renderUnitPriceHint(row)}
                          </>
                        )
                      }
                    </td>
                    <td className={styles.quantityCell}>
                      {isPreview
                        ? (
                          <PreviewField
                            label=''
                            value={row.quantity != null ? String(row.quantity) : '1'}
                          />
                        )
                        : (
                          <InputNumber
                            name={`rentals.${index}.quantity`}
                            control={control}
                            allowFloat
                            min='0'
                            handleChange={(value) => handleQuantityChange(index, value)}
                          />
                        )
                      }
                    </td>
                    <td className={styles.durationCell}>
                      {isPreview
                        ? <PreviewField label='' value={getDurationLabel(row.duration)} />
                        : (
                          <StyledSelect
                            name={`rentals.${index}.duration`}
                            control={control}
                            options={durations}
                            placeholder='Durée'
                            isDisabled={!row.productId}
                            menuInPortal
                            handleChange={(opt) => handleDurationChange(index, opt)}
                          />
                        )
                      }
                    </td>
                    <td className={styles.totalCell}>
                      {renderLineTotal(row)}
                    </td>
                    {!isPreview && (
                      <td className={styles.actionsCell}>
                        <button
                          type="button"
                          className={styles.removeRow}
                          onClick={() => remove(index)}
                          aria-label="Supprimer la ligne"
                        >
                          {IoTrash({ size: 18 })}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!isPreview && (
        <button
          type="button"
          className={styles.addRow}
          onClick={() => append(emptyRentalRow())}
        >
          Ajouter une ligne
        </button>
      )}
    </div>
  );
};

export default RentalsForm;
